import { StructuredJsonFieldDef } from '../../../shared/design-system/structured-json-form.model';

export const SEARCH_OPS_SITE_KINDS = ['ain_storefront', 'external'] as const;
export const SEARCH_OPS_CMS_TYPES = ['ain', 'wordpress', 'shopify', 'custom', 'unknown'] as const;
export const SEARCH_OPS_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export const SEARCH_OPS_LOCALES = ['fa-IR', 'en-US', 'ar-IQ'] as const;
export const SEARCH_OPS_GOAL_METRICS = [
  'organic_sessions',
  'core_keyword_avg_rank',
  'indexed_pages',
  'organic_revenue',
  'ads_roas'
] as const;
export const SEARCH_OPS_WORK_TYPES = [
  'research', 'onpage', 'technical', 'content', 'offpage', 'local', 'sem', 'reporting', 'apply'
] as const;
export const SEARCH_OPS_WORK_STATUSES = [
  'idea', 'ready', 'this_cycle', 'doing', 'blocked', 'done', 'cancelled'
] as const;
export const SEARCH_OPS_WORK_SOURCES = [
  'manual', 'audit', 'gsc', 'agent', 'coworker', 'crawl', 'client'
] as const;
export const SEARCH_OPS_CYCLE_KINDS = ['week', 'month', 'quarter'] as const;
export const SEARCH_OPS_COVERAGE_MODES = ['assist_human', 'covers_unfilled'] as const;
export const SEARCH_OPS_AUTONOMIES = ['suggest', 'assisted'] as const;
export const SEARCH_OPS_CADENCES = ['daily', 'weekly', 'monthly', 'cron'] as const;
export const SEARCH_OPS_TASK_TRANSITIONS: Record<string, string[]> = {
  idea: ['ready', 'cancelled'],
  ready: ['this_cycle', 'doing', 'cancelled'],
  this_cycle: ['doing', 'blocked', 'cancelled'],
  doing: ['blocked', 'done', 'this_cycle'],
  blocked: ['doing', 'this_cycle', 'cancelled'],
  done: [],
  cancelled: []
};
export const SEARCH_OPS_KEYWORD_INTENTS = [
  'informational',
  'commercial',
  'transactional',
  'navigational',
  'local'
] as const;
export const SEARCH_OPS_KEYWORD_STATUSES = ['track', 'ignore', 'owned', 'opportunity'] as const;
export const SEARCH_OPS_MAP_ROLES = ['primary', 'secondary', 'supporting'] as const;
export const SEARCH_OPS_CLUSTER_STATUSES = ['none', 'briefed', 'in_ace', 'published', 'decaying'] as const;
export const SEARCH_OPS_URL_TEMPLATES = ['pdp', 'plp', 'article', 'home', 'landing', 'other'] as const;
export const SEARCH_OPS_ISSUE_SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export const SEARCH_OPS_ISSUE_CATEGORIES = [
  'status', 'indexation', 'canonical', 'duplicate', 'redirect', 'hreflang', 'schema', 'cwv', 'security', 'content'
] as const;
export const SEARCH_OPS_ISSUE_STATUSES = ['open', 'tasked', 'done', 'accepted_risk', 'false_positive'] as const;
export const SEARCH_OPS_REDIRECT_TYPES = ['301', '302', '410'] as const;
export const SEARCH_OPS_REDIRECT_STATUSES = ['draft', 'approved', 'applied', 'failed', 'verified'] as const;
export const SEARCH_OPS_APPLY_STATUSES = ['draft', 'awaiting_approval'] as const;
export const SEARCH_OPS_PROSPECT_STATUSES = [
  'identified',
  'qualified',
  'outreach',
  'linked',
  'declined',
  'do_not_contact'
] as const;
export const SEARCH_OPS_OUTREACH_CHANNELS = ['email', 'form', 'social'] as const;
export const SEARCH_OPS_OUTREACH_RESULTS = [
  'drafted',
  'sent',
  'followed_up',
  'replied',
  'linked',
  'declined',
  'do_not_contact'
] as const;
export const SEARCH_OPS_BACKLINK_RELS = ['follow', 'nofollow', 'ugc', 'sponsored'] as const;
export const SEARCH_OPS_BACKLINK_STATUSES = ['live', 'lost', 'broken'] as const;
export const SEARCH_OPS_BACKLINK_ACQUIRED = ['outreach', 'pr', 'unsolicited', 'unknown'] as const;
export const SEARCH_OPS_DISAVOW_KINDS = ['domain', 'url'] as const;
export const SEARCH_OPS_DISAVOW_STATUSES = ['draft', 'awaiting_approval'] as const;
export const SEARCH_OPS_LOCAL_STATUSES = ['draft', 'active', 'paused'] as const;
export const SEARCH_OPS_NAP_MATCH = ['exact', 'partial', 'mismatch', 'missing'] as const;
export const SEARCH_OPS_REVIEW_ACTIONS = ['reply', 'flag', 'escalate'] as const;
export const SEARCH_OPS_REVIEW_STATUSES = ['open', 'drafted', 'done'] as const;

export interface SearchKeywordUrlMapDto {
  id: string;
  keywordId: string;
  phrase: string;
  locale: string;
  targetUrl: string;
  urlInventoryId: string | null;
  role: string;
  mappedAtUtc: string;
  mappedByUserId: string | null;
  cannibalizationWarning: boolean;
}

export interface SearchKeywordListItemDto {
  id: string;
  searchPropertyId: string;
  phrase: string;
  locale: string;
  intent: string;
  volume: number | null;
  difficulty: number | null;
  cpc: number | null;
  status: string;
  seasonalityNote: string;
  brandOverlap: boolean;
  clusterId: string | null;
  clusterName: string | null;
  cannibalizationWarning: boolean;
  maps: SearchKeywordUrlMapDto[];
}

export interface SearchKeywordClusterListItemDto {
  id: string;
  searchPropertyId: string;
  name: string;
  pillarKeywordId: string | null;
  pillarPhrase: string | null;
  intent: string | null;
  plannedUrl: string | null;
  contentStatus: string;
  aceBriefId: string | null;
  aceTaskId: string | null;
  keywordCount: number;
}

export interface SearchKeywordClusterDetailDto {
  cluster: SearchKeywordClusterListItemDto;
  keywords: SearchKeywordListItemDto[];
  cannibalizationWarning: boolean;
}

export interface SearchKeywordImportResultDto {
  created: number;
  updated: number;
  total: number;
}

export interface SearchDecayItemDto {
  keywordId: string;
  searchPropertyId: string;
  clusterId: string | null;
  phrase: string;
  locale: string;
  targetUrl: string | null;
  previousRank: number | null;
  currentRank: number | null;
  rankDrop: number | null;
  reason: string;
  contentStatus: string | null;
}

export interface CreateAceBriefFromClusterResultDto {
  clusterId: string;
  briefId: string;
  taskId: string;
  existing: boolean;
}

export interface UpsertSearchKeywordRequest {
  phrase: string;
  locale: string;
  intent?: string | null;
  volume?: number | null;
  difficulty?: number | null;
  cpc?: number | null;
  status?: string | null;
  seasonalityNote?: string | null;
  brandOverlap: boolean;
  clusterId?: string | null;
}

export interface UpsertSearchKeywordClusterRequest {
  name: string;
  pillarKeywordId?: string | null;
  intent?: string | null;
  plannedUrl?: string | null;
  contentStatus?: string | null;
}

export interface UpsertSearchKeywordUrlMapRequest {
  keywordId: string;
  targetUrl: string;
  role: string;
}

export interface SearchPositionCatalogItemDto {
  code: string;
  defaultTaskType: string;
  sampleKpi: string;
}

export interface SearchPositionAssignmentDto {
  id: string;
  seatId: string;
  positionCode: string;
}

export interface SearchTeamSeatDto {
  id: string;
  searchPropertyId: string;
  userId: string;
  weeklyHourCapacity: number;
  active: boolean;
  positions: SearchPositionAssignmentDto[];
}

export interface SearchPropertyGoalDto {
  id: string;
  searchPropertyId: string;
  cycleId: string | null;
  metricCode: string;
  baseline: number;
  target: number;
  unit: string;
  ownerPositionCode: string | null;
}

export interface SearchSiteAdapterBindingDto {
  id: string;
  searchPropertyId: string;
  adapterKey: string;
  configJson: string;
  secretRef: string | null;
  status: string;
}

export interface SearchPropertyListItemDto {
  id: string;
  name: string;
  primaryDomain: string;
  siteKind: string;
  cmsType: string;
  status: string;
  adapterKey: string;
  seatCount: number;
}

export interface SearchPropertyDetailDto {
  id: string;
  name: string;
  primaryDomain: string;
  siteKind: string;
  cmsType: string;
  localesJson: string;
  status: string;
  storefrontBindingJson: string;
  onboardingJson: string;
  adapter: SearchSiteAdapterBindingDto;
  seats: SearchTeamSeatDto[];
  goals: SearchPropertyGoalDto[];
}

export interface SearchWorkHomeDto {
  properties: SearchPropertyListItemDto[];
  selectedProperty: SearchPropertyDetailDto | null;
  myPositions: SearchPositionAssignmentDto[];
  positionCatalog: SearchPositionCatalogItemDto[];
  seoProfilesPath: string;
  coworkerInbox: SearchRecommendationDto[];
  coworkerReadyTasks: SearchTaskListItemDto[];
  executiveSnapshot: SearchExecutiveSnapshotDto | null;
}

export interface SearchExecutiveSnapshotDto {
  clicks: number | null;
  coreRank: number | null;
  openCriticalIssues: number | null;
  periodEndUtc: string | null;
}

export interface SearchCycleDto {
  id: string;
  searchPropertyId: string;
  kind: string;
  startAtUtc: string;
  endAtUtc: string;
  status: string;
  title: string;
}

export interface SearchBacklogItemDto {
  id: string;
  searchPropertyId: string;
  cycleId: string | null;
  positionCode: string;
  type: string;
  source: string;
  sourceRef: string | null;
  impact: number;
  effort: number;
  status: string;
  title: string;
  description: string;
  recommendationId: string | null;
}

export interface SearchTaskListItemDto {
  id: string;
  searchPropertyId: string;
  backlogItemId: string | null;
  cycleId: string | null;
  title: string;
  assigneeUserId: string | null;
  status: string;
  dueAtUtc: string | null;
  checklistTemplateKey: string | null;
  source: string;
  positionCode: string;
  type: string;
  coworkerId: string | null;
  briefId: string | null;
}

export interface SearchWorklogDto {
  id: string;
  taskId: string;
  userId: string;
  minutes: number;
  note: string;
  createdAtUtc: string;
}

export interface SearchChecklistRunDto {
  id: string;
  taskId: string;
  templateKey: string;
  resultsJson: string;
  score: number;
}

export interface SearchTaskDetailDto {
  task: SearchTaskListItemDto;
  checklist: SearchChecklistRunDto | null;
  worklogs: SearchWorklogDto[];
}

export interface SearchRecommendationDto {
  id: string;
  searchPropertyId: string;
  kind: string;
  payloadJson: string;
  citationJson: string;
  status: string;
  agentRunId: string | null;
  coworkerId: string | null;
}

export interface SearchChecklistTemplateItemDto {
  key: string;
  label: string;
}

export interface SearchChecklistTemplateDto {
  key: string;
  title: string;
  items: SearchChecklistTemplateItemDto[];
}

export interface SearchCoworkerDto {
  id: string;
  searchPropertyId: string;
  name: string;
  ownerUserId: string;
  positionCodes: string[];
  coverageMode: string;
  agentDefinitionKey: string;
  autonomy: string;
  status: string;
  killSwitch: boolean;
}

export interface SearchCoworkerPlayDto {
  id: string;
  coworkerId: string;
  playKey: string;
  enabled: boolean;
  configJson: string;
}

export interface SearchCoworkerScheduleDto {
  id: string;
  coworkerId: string;
  cadence: string;
  cronExpr: string | null;
  timeZone: string;
  localTime: string;
  weekday: number | null;
  monthDay: number | null;
  nextRunAtUtc: string;
  lastRunAtUtc: string | null;
  enabled: boolean;
}

export interface SearchCoworkerRunDto {
  id: string;
  coworkerId: string;
  scheduleId: string;
  periodKey: string;
  status: string;
  statsJson: string;
  createdAtUtc: string;
}

export interface CreateSearchCycleRequest {
  kind: string;
  startAtUtc: string;
  endAtUtc: string;
  title: string;
  status?: string | null;
}

export interface CreateSearchBacklogItemRequest {
  cycleId?: string | null;
  positionCode: string;
  type: string;
  source?: string | null;
  sourceRef?: string | null;
  impact: number;
  effort: number;
  title: string;
  description?: string | null;
  status?: string | null;
}

export interface CreateSearchTaskRequest {
  backlogItemId?: string | null;
  cycleId?: string | null;
  title: string;
  assigneeUserId?: string | null;
  status?: string | null;
  dueAtUtc?: string | null;
  checklistTemplateKey?: string | null;
  source?: string | null;
  positionCode: string;
  type: string;
}

export interface CreateSearchCoworkerRequest {
  searchPropertyId: string;
  name: string;
  ownerUserId: string;
  positionCodes: string[];
  coverageMode: string;
  autonomy: string;
  killSwitch: boolean;
  agentDefinitionKey?: string | null;
  cadence?: string | null;
  timeZone?: string | null;
  localTime?: string | null;
  weekday?: number | null;
  monthDay?: number | null;
  scheduleEnabled?: boolean | null;
}

export interface UpdateSearchCoworkerRequest {
  name: string;
  ownerUserId: string;
  positionCodes: string[];
  coverageMode: string;
  autonomy: string;
  killSwitch: boolean;
  agentDefinitionKey?: string | null;
}

export interface UpsertSearchCoworkerScheduleRequest {
  cadence: string;
  cronExpr?: string | null;
  timeZone: string;
  localTime: string;
  weekday?: number | null;
  monthDay?: number | null;
  enabled: boolean;
}

export interface UpsertSearchPropertyRequest {
  name: string;
  primaryDomain: string;
  siteKind: string;
  cmsType: string;
  localesJson?: string | null;
  status?: string | null;
  storefrontBindingJson?: string | null;
  onboardingJson?: string | null;
}

export interface UpsertSearchTeamSeatRequest {
  userId: string;
  weeklyHourCapacity: number;
  active: boolean;
  positionCodes: string[];
}

export interface UpsertSearchPropertyGoalRequest {
  cycleId?: string | null;
  metricCode: string;
  baseline: number;
  target: number;
  unit: string;
  ownerPositionCode?: string | null;
}

export const searchOpsStorefrontBindingFields: StructuredJsonFieldDef[] = [
  { key: 'storefrontKey', type: 'string', labelKey: 'growth.searchops.fields.storefrontKey' },
  { key: 'storefrontPublicationId', type: 'string', labelKey: 'growth.searchops.fields.storefrontPublicationId' }
];

export const searchOpsExternalAdapterFields: StructuredJsonFieldDef[] = [
  { key: 'endpointUrl', type: 'string', labelKey: 'growth.searchops.fields.endpointUrl' },
  { key: 'allowedDomain', type: 'string', labelKey: 'growth.searchops.fields.allowedDomain' },
  { key: 'ackTimeoutMinutes', type: 'number', labelKey: 'growth.searchops.fields.ackTimeoutMinutes' },
  { key: 'cmsAdapter', type: 'string', labelKey: 'growth.searchops.fields.cmsAdapter' }
];

export interface SearchExternalAdapterConfigureResultDto {
  adapter: SearchSiteAdapterBindingDto;
  eventHubSubscriptionId: string;
  endpointUrl: string;
  apiKey: string | null;
  webhookSigningSecret: string | null;
  ackTimeoutMinutes: number;
}

export interface SearchOpsWebhookDeliveryListItem {
  id: string;
  subscriptionId: string;
  outboxMessageId: string;
  eventId: string;
  eventType: string;
  attempt: number;
  signatureKeyId: string;
  statusCode?: number | null;
  isSuccess: boolean;
  responseSnippet: string;
  errorMessage?: string | null;
  deliveredAtUtc?: string | null;
}

export interface SearchOpsStorefrontBinding {
  storefrontKey: string;
  storefrontPublicationId: string;
}

export function parseSearchOpsStorefrontBinding(json: string | null | undefined): SearchOpsStorefrontBinding {
  try {
    const parsed = JSON.parse(json || '{}') as Record<string, unknown>;
    return {
      storefrontKey: typeof parsed['storefrontKey'] === 'string' ? parsed['storefrontKey'] : '',
      storefrontPublicationId: typeof parsed['storefrontPublicationId'] === 'string' ? parsed['storefrontPublicationId'] : ''
    };
  } catch {
    return { storefrontKey: '', storefrontPublicationId: '' };
  }
}

export function serializeSearchOpsStorefrontBinding(binding: SearchOpsStorefrontBinding): string {
  const payload: Record<string, string> = {};
  if (binding.storefrontKey.trim())
    payload['storefrontKey'] = binding.storefrontKey.trim();
  if (binding.storefrontPublicationId.trim())
    payload['storefrontPublicationId'] = binding.storefrontPublicationId.trim();
  return JSON.stringify(payload);
}

export const searchOpsOnboardingFields: StructuredJsonFieldDef[] = [
  { key: 'markets', type: 'string', labelKey: 'growth.searchops.fields.markets' },
  { key: 'competitors', type: 'string', labelKey: 'growth.searchops.fields.competitors' },
  { key: 'brandTerms', type: 'string', labelKey: 'growth.searchops.fields.brandTerms' },
  { key: 'convertingActions', type: 'textarea', labelKey: 'growth.searchops.fields.convertingActions' },
  { key: 'hasGscAccess', type: 'boolean', labelKey: 'growth.searchops.fields.hasGscAccess' },
  { key: 'hasGaAccess', type: 'boolean', labelKey: 'growth.searchops.fields.hasGaAccess' },
  { key: 'hasAdsAccess', type: 'boolean', labelKey: 'growth.searchops.fields.hasAdsAccess' },
  { key: 'hasCmsAccess', type: 'boolean', labelKey: 'growth.searchops.fields.hasCmsAccess' },
  { key: 'notes', type: 'textarea', labelKey: 'growth.searchops.fields.notes' }
];

export const searchOpsEvidenceFields: StructuredJsonFieldDef[] = [
  { key: 'finding', type: 'textarea', labelKey: 'growth.searchops.fields.finding' },
  { key: 'expected', type: 'string', labelKey: 'growth.searchops.fields.expected' },
  { key: 'actual', type: 'string', labelKey: 'growth.searchops.fields.actual' },
  { key: 'recommendation', type: 'textarea', labelKey: 'growth.searchops.fields.recommendation' }
];

export const searchOpsPatchFields: StructuredJsonFieldDef[] = [
  { key: 'title', type: 'string', labelKey: 'growth.searchops.fields.pageTitle' },
  { key: 'metaDescription', type: 'textarea', labelKey: 'growth.searchops.fields.metaDescription' },
  { key: 'canonical', type: 'string', labelKey: 'growth.searchops.fields.canonical' },
  { key: 'robots', type: 'string', labelKey: 'growth.searchops.fields.robots' }
];

export const searchOpsNapFields: StructuredJsonFieldDef[] = [
  { key: 'name', type: 'string', labelKey: 'growth.searchops.fields.napName' },
  { key: 'addressLine', type: 'string', labelKey: 'growth.searchops.fields.napAddress' },
  { key: 'city', type: 'string', labelKey: 'growth.searchops.fields.napCity' },
  { key: 'region', type: 'string', labelKey: 'growth.searchops.fields.napRegion' },
  { key: 'postalCode', type: 'string', labelKey: 'growth.searchops.fields.napPostal' },
  { key: 'country', type: 'string', labelKey: 'growth.searchops.fields.napCountry' },
  { key: 'phone', type: 'string', labelKey: 'growth.searchops.fields.napPhone' },
  { key: 'website', type: 'string', labelKey: 'growth.searchops.fields.napWebsite' }
];

export const searchOpsOffpageEvidenceFields: StructuredJsonFieldDef[] = [
  { key: 'notes', type: 'textarea', labelKey: 'growth.searchops.fields.notes' },
  { key: 'pitch', type: 'textarea', labelKey: 'growth.searchops.fields.pitch' }
];

export const searchOpsReviewEvidenceFields: StructuredJsonFieldDef[] = [
  { key: 'reviewText', type: 'textarea', labelKey: 'growth.searchops.fields.reviewText' },
  { key: 'draftReply', type: 'textarea', labelKey: 'growth.searchops.fields.draftReply' },
  { key: 'rating', type: 'string', labelKey: 'growth.searchops.fields.rating' }
];

export interface SearchLinkProspectDto {
  id: string;
  searchPropertyId: string;
  domain: string;
  targetUrl: string | null;
  relevanceScore: number;
  qualityScore: number;
  combinedScore: number;
  contactEmail: string | null;
  contactPhone: string | null;
  contactMasked: boolean;
  status: string;
  doNotContact: boolean;
  evidenceJson: string;
  taskId: string | null;
}

export interface SearchOutreachDto {
  id: string;
  searchPropertyId: string;
  prospectId: string;
  prospectDomain: string | null;
  channel: string;
  templateKey: string | null;
  lastTouchAtUtc: string | null;
  result: string;
  producedBacklinkId: string | null;
  evidenceJson: string;
  taskId: string | null;
}

export interface SearchOutreachFunnelBucketDto {
  result: string;
  count: number;
}

export interface SearchBacklinkDto {
  id: string;
  searchPropertyId: string;
  sourceUrl: string;
  targetUrl: string;
  anchor: string | null;
  rel: string;
  status: string;
  acquiredVia: string;
  firstSeenUtc: string;
  lastSeenUtc: string;
  outreachId: string | null;
}

export interface SearchBacklinkImportRowDto {
  sourceUrl: string;
  targetUrl: string;
  anchor: string | null;
  rel: string;
  status: string;
  acquiredVia: string;
  error: string | null;
  id: string | null;
}

export interface SearchBacklinkImportResultDto {
  accepted: number;
  rejected: number;
  rows: SearchBacklinkImportRowDto[];
}

export interface SearchDisavowItemDto {
  id: string;
  searchPropertyId: string;
  entity: string;
  entityKind: string;
  reason: string;
  status: string;
  workflowIntentId: string | null;
  submittedAtUtc: string | null;
}

export interface SearchLocalProfileDto {
  id: string;
  searchPropertyId: string;
  napJson: string;
  externalGbpId: string | null;
  status: string;
}

export interface SearchCitationDto {
  id: string;
  searchPropertyId: string;
  localProfileId: string;
  directory: string;
  url: string;
  napJson: string;
  napMatchStatus: string;
}

export interface SearchReviewTaskDto {
  id: string;
  searchPropertyId: string;
  localProfileId: string;
  externalId: string;
  action: string;
  status: string;
  evidenceJson: string;
  taskId: string | null;
}

export interface SearchUrlInventoryDto {
  id: string;
  searchPropertyId: string;
  url: string;
  canonicalUrl: string | null;
  template: string;
  httpStatus: number | null;
  indexable: boolean;
  seoProfileId: string | null;
  contentAssetId: string | null;
  offeringId: string | null;
  lastCrawledAtUtc: string | null;
  cwvJson: string;
}

export interface SearchCrawlRunDto {
  id: string;
  searchPropertyId: string;
  provider: string;
  startedAtUtc: string;
  completedAtUtc: string | null;
  status: string;
  statsJson: string;
  issueCount: number;
}

export interface SearchIssueDto {
  id: string;
  searchPropertyId: string;
  crawlRunId: string | null;
  urlInventoryId: string | null;
  url: string | null;
  severity: string;
  category: string;
  status: string;
  evidenceJson: string;
  suggestedTaskType: string;
  taskId: string | null;
  seoProfileId: string | null;
}

export interface SearchRedirectDto {
  id: string;
  searchPropertyId: string;
  fromPath: string;
  toUrl: string;
  redirectType: string;
  reason: string | null;
  status: string;
  applyJobId: string | null;
}

export interface SearchRedirectImportRowDto {
  fromPath: string;
  toUrl: string;
  redirectType: string;
  reason: string | null;
  error: string | null;
  id: string | null;
}

export interface SearchRedirectImportResultDto {
  dryRun: boolean;
  accepted: number;
  rejected: number;
  rows: SearchRedirectImportRowDto[];
}

export interface SearchChangeLogDto {
  id: string;
  searchPropertyId: string;
  occurredAtUtc: string;
  summary: string;
  source: string;
  relatedIssueId: string | null;
}

export interface SearchApplyJobDto {
  id: string;
  searchPropertyId: string;
  urlInventoryId: string | null;
  patchJson: string;
  status: string;
  idempotencyKey: string;
  workflowIntentId: string | null;
  adapterReceipt: string | null;
  dryRun: boolean;
}

export interface SearchOnpageQaResultDto {
  urlInventoryId: string;
  score: number;
  passed: boolean;
  seoProfileId: string | null;
  applyJobId: string | null;
}

export interface UpsertSearchUrlInventoryRequest {
  url: string;
  canonicalUrl?: string | null;
  template?: string | null;
  httpStatus?: number | null;
  indexable: boolean;
  seoProfileId?: string | null;
  contentAssetId?: string | null;
  offeringId?: string | null;
  cwvJson?: string | null;
}

export const SEARCH_OPS_NEGATIVE_MATCH_TYPES = ['exact', 'phrase', 'broad'] as const;
export const SEARCH_OPS_NEGATIVE_SCOPES = ['account', 'campaign', 'adgroup'] as const;
export const SEARCH_OPS_ADS_BRIEF_STATUSES = ['draft', 'ready', 'used'] as const;

export const searchOpsSemNotesFields: StructuredJsonFieldDef[] = [
  { key: 'period', type: 'select', labelKey: 'growth.searchops.fields.budgetPeriod', options: [
    { value: 'week', labelKey: 'growth.searchops.budgetPeriod.week' },
    { value: 'month', labelKey: 'growth.searchops.budgetPeriod.month' }
  ] },
  { key: 'promotionNote', type: 'textarea', labelKey: 'growth.searchops.fields.promotionNote' },
  { key: 'calendarNote', type: 'textarea', labelKey: 'growth.searchops.fields.calendarNote' }
];

export const searchOpsRsaHeadlineFields: StructuredJsonFieldDef[] = [
  { key: 'headline1', type: 'string', labelKey: 'growth.searchops.fields.headline1' },
  { key: 'headline2', type: 'string', labelKey: 'growth.searchops.fields.headline2' },
  { key: 'headline3', type: 'string', labelKey: 'growth.searchops.fields.headline3' },
  { key: 'headline4', type: 'string', labelKey: 'growth.searchops.fields.headline4' },
  { key: 'headline5', type: 'string', labelKey: 'growth.searchops.fields.headline5' }
];

export const searchOpsRsaDescriptionFields: StructuredJsonFieldDef[] = [
  { key: 'description1', type: 'textarea', labelKey: 'growth.searchops.fields.description1' },
  { key: 'description2', type: 'textarea', labelKey: 'growth.searchops.fields.description2' }
];

export interface SearchSemAccountOptionDto {
  id: string;
  providerKey: string;
  nationCode: string;
  isEnabled: boolean;
  killSwitchEnabled: boolean;
}

export interface SearchSemCampaignLinkOptionDto {
  id: string;
  accountId: string;
  externalCampaignId: string;
  objective: string;
  budgetCap: number;
  approvalState: string;
  status: string;
  approvalReason: string | null;
  requestedSpend: number | null;
}

export interface SearchSemWorkbookDto {
  id: string;
  searchPropertyId: string;
  paidMediaAccountId: string;
  accountProviderKey: string | null;
  accountNationCode: string | null;
  notes: string;
}

export interface SearchNegativeKeywordDto {
  id: string;
  searchPropertyId: string;
  phrase: string;
  matchType: string;
  scope: string;
  campaignLinkId: string | null;
  externalCampaignId: string | null;
}

export interface SearchNegativeExportDto {
  fileName: string;
  csv: string;
}

export interface SearchAdsBriefDto {
  id: string;
  searchPropertyId: string;
  clusterId: string | null;
  clusterName: string | null;
  campaignLinkId: string | null;
  externalCampaignId: string | null;
  headlineJson: string;
  descriptionJson: string;
  status: string;
  taskId: string | null;
}

export interface SearchSearchTermImportResultDto {
  keywordsCreated: number;
  negativesCreated: number;
  rejected: number;
  rows: Array<{
    phrase: string;
    action: string;
    error: string | null;
    keywordId: string | null;
    negativeId: string | null;
  }>;
}

export interface SearchSemBudgetWarningDto {
  campaignLinkId: string;
  externalCampaignId: string;
  budgetCap: number;
  requestedSpend: number | null;
  observedSpend: number | null;
  approvalState: string;
  code: string;
  message: string;
}

export const SEARCH_OPS_SCORECARD_SOURCES = ['manual', 'sandbox'] as const;
export const SEARCH_OPS_ANOMALY_STATUSES = ['open', 'watching', 'tasked', 'dismissed'] as const;
export const SEARCH_OPS_REPORT_STATUSES = ['draft', 'published'] as const;
export const SEARCH_OPS_REPORT_LOCALES = ['fa-IR', 'en-US'] as const;
export const SEARCH_OPS_ALGO_ENGINES = ['google', 'bing', 'other'] as const;

export const searchOpsScorecardMetricFields: StructuredJsonFieldDef[] = [
  { key: 'clicks', type: 'number', labelKey: 'growth.searchops.fields.clicks' },
  { key: 'impressions', type: 'number', labelKey: 'growth.searchops.fields.impressions' },
  { key: 'ctr', type: 'number', labelKey: 'growth.searchops.fields.ctr' },
  { key: 'coreRank', type: 'number', labelKey: 'growth.searchops.fields.coreRank' },
  { key: 'indexedPages', type: 'number', labelKey: 'growth.searchops.fields.indexedPages' },
  { key: 'openCriticalIssues', type: 'number', labelKey: 'growth.searchops.fields.openCriticalIssues' },
  { key: 'newLinks', type: 'number', labelKey: 'growth.searchops.fields.newLinks' },
  { key: 'spend', type: 'number', labelKey: 'growth.searchops.fields.spend' },
  { key: 'roas', type: 'number', labelKey: 'growth.searchops.fields.roas' }
];

export const searchOpsAnomalyHypothesisFields: StructuredJsonFieldDef[] = [
  { key: 'rule', type: 'string', labelKey: 'growth.searchops.fields.anomalyRule' },
  { key: 'previousClicks', type: 'number', labelKey: 'growth.searchops.fields.previousClicks' },
  { key: 'currentClicks', type: 'number', labelKey: 'growth.searchops.fields.currentClicks' },
  { key: 'dropRatio', type: 'number', labelKey: 'growth.searchops.fields.dropRatio' },
  { key: 'threshold', type: 'number', labelKey: 'growth.searchops.fields.dropThreshold' }
];

export const searchOpsReportBodyFields: StructuredJsonFieldDef[] = [
  { key: 'executiveSummary', type: 'textarea', labelKey: 'growth.searchops.fields.executiveSummary' },
  { key: 'keywords', type: 'textarea', labelKey: 'growth.searchops.fields.keywordsSection' },
  { key: 'technical', type: 'textarea', labelKey: 'growth.searchops.fields.technicalSection' },
  { key: 'links', type: 'textarea', labelKey: 'growth.searchops.fields.linksSection' },
  { key: 'ads', type: 'textarea', labelKey: 'growth.searchops.fields.adsSection' },
  { key: 'nextPlan', type: 'textarea', labelKey: 'growth.searchops.fields.nextPlan' },
  { key: 'risks', type: 'textarea', labelKey: 'growth.searchops.fields.risks' }
];

export const searchOpsAlgoEffectFields: StructuredJsonFieldDef[] = [
  { key: 'impact', type: 'select', labelKey: 'growth.searchops.fields.observedImpact', options: [
    { value: 'none', labelKey: 'growth.searchops.algoImpact.none' },
    { value: 'drop', labelKey: 'growth.searchops.algoImpact.drop' },
    { value: 'gain', labelKey: 'growth.searchops.algoImpact.gain' },
    { value: 'mixed', labelKey: 'growth.searchops.algoImpact.mixed' }
  ] },
  { key: 'notes', type: 'textarea', labelKey: 'growth.searchops.fields.observedNotes' }
];

export interface SearchScorecardMetricDto {
  code: string;
  current: number | null;
  previous: number | null;
  goalTarget: number | null;
  vsPreviousDelta: number | null;
  vsGoalDelta: number | null;
}

export interface SearchScorecardSnapshotDto {
  id: string;
  searchPropertyId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  source: string;
  metricsJson: string;
  comparisons: SearchScorecardMetricDto[];
}

export interface SearchScorecardBoardDto {
  latest: SearchScorecardSnapshotDto | null;
  previous: SearchScorecardSnapshotDto | null;
  snapshots: SearchScorecardSnapshotDto[];
}

export interface SearchAnomalyDto {
  id: string;
  searchPropertyId: string;
  metricCode: string;
  detectedAtUtc: string;
  periodKey: string;
  hypothesisJson: string;
  status: string;
  taskId: string | null;
}

export interface SearchReportWorkDoneItemDto {
  taskId: string;
  title: string;
  type: string;
  positionCode: string;
  status: string;
}

export interface SearchClientReportDto {
  id: string;
  searchPropertyId: string;
  cycleId: string;
  cycleTitle: string | null;
  locale: string;
  bodyJson: string;
  status: string;
  publishedAtUtc: string | null;
  workDone: SearchReportWorkDoneItemDto[];
  printHtml: string;
}

export interface SearchAlgoLogEntryDto {
  id: string;
  searchPropertyId: string;
  engine: string;
  notedOnUtc: string;
  title: string;
  observedEffectJson: string;
}
